import { FileText, MapPinned, User } from 'lucide-react'
import { GrowerAttachmentsField } from './attachments/grower-attachments-field'
import { useReadonlyAttachments } from './attachments/use-readonly-attachments'
import { toDateInput } from './grower-form-values'
import { ReadonlyField } from './readonly/readonly-field'
import { GrowerReadonlyAreas } from './readonly/grower-readonly-areas'
import { GrowerFacesCard } from './faces/grower-faces-card'
import type { Grower } from '@/lib/api/growers-api'
import { FormSection } from '@/components/forms/form-section'
import { GoogleMapsProvider } from '@/components/maps/google-maps-provider'
import { Label } from '@/components/ui/label'
import { formatVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

type Props = {
    grower: Grower
    /**
     * Ai đang xem. Quyết định route tải giấy tờ: tài khoản hộ không có
     * `files.read` nên phải đi route riêng của hộ, còn nhân viên gọi route đó
     * thì bị 404 (nó chỉ nhận đúng hộ đang đăng nhập) → ảnh giấy tờ hiện thành
     * ảnh vỡ. Mặc định giữ 'household' như hành vi cũ.
     */
    scope?: 'household' | 'admin'
}

/**
 * Hồ sơ hộ ở chế độ chỉ xem — dùng cho tài khoản hộ tự xem và cho nhân viên
 * không có quyền sửa.
 *
 * Cố ý KHÔNG phải màn sửa bị khoá: mọi thông tin hiện dạng chữ, không có ô
 * nhập, không có <form>, không có mutation hook, nên không có đường nào lưu
 * được dù một control lỡ bật. Ô nhập xám chỉ khiến người đọc tưởng mình sửa
 * được rồi thắc mắc vì sao không gõ được.
 */
export function GrowerReadonlyView({ grower, scope = 'household' }: Props) {
    const attachments = useReadonlyAttachments(
        grower.id,
        grower.files,
        'profile',
        scope,
    )

    return (
        <div className="mx-auto max-w-[1180px] space-y-5">
            <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
                <FormSection icon={<User />} title={m.grower_general_section()}>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        <ReadonlyField
                            label={m.grower_name()}
                            value={grower.name}
                            testId="grower-name"
                        />
                        <ReadonlyField
                            label={m.grower_nickname()}
                            value={grower.nickname}
                            testId="grower-nickname"
                        />
                        <ReadonlyField
                            label={m.grower_phone()}
                            value={grower.phone}
                        />
                        <ReadonlyField
                            label={m.grower_national_id()}
                            value={grower.national_id}
                        />
                        <ReadonlyField
                            label={m.grower_birthday()}
                            value={dateText(grower.birthday)}
                        />
                        <ReadonlyField
                            label={m.grower_national_id_issued_date()}
                            value={dateText(grower.national_id_issued_date)}
                        />
                        <ReadonlyField
                            label={m.grower_national_id_issued_place()}
                            value={grower.national_id_issued_place}
                        />
                        <ReadonlyField
                            label={m.grower_address()}
                            value={grower.address}
                            className="whitespace-pre-line sm:col-span-2"
                        />
                    </div>
                </FormSection>

                <FormSection
                    icon={<FileText />}
                    title={m.grower_profile_section()}
                >
                    <div className="space-y-5">
                        <div className="space-y-1.5">
                            <Label>{m.grower_documents()}</Label>
                            <GrowerAttachmentsField
                                draft={attachments}
                                readOnly
                                label={m.grower_documents()}
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <ReadonlyField
                                label={m.grower_bank_number()}
                                value={grower.bank_number}
                            />
                            <ReadonlyField
                                label={m.grower_bank_name()}
                                value={grower.bank_name}
                            />
                            <ReadonlyField
                                label={m.grower_bank_account()}
                                value={grower.bank_account}
                                className="sm:col-span-2"
                            />
                        </div>
                    </div>
                </FormSection>
            </div>

            <FormSection icon={<MapPinned />} title={m.grower_areas()}>
                <GoogleMapsProvider>
                    <GrowerReadonlyAreas
                        areas={grower.areas ?? []}
                        growerId={grower.id}
                        growerName={grower.name}
                        growerNickname={grower.nickname}
                        files={grower.files}
                        scope={scope}
                    />
                </GoogleMapsProvider>
            </FormSection>

            {/* Nhận diện khuôn mặt: chỉ tóm tắt + lối vào màn cấu hình. Ảnh
                khuôn mặt là dữ liệu nhạy cảm nên không hiện ngay ở đây. */}
            <GrowerFacesCard
                growerId={grower.id}
                scope={scope === 'admin' ? 'staff' : 'my'}
            />
        </div>
    )
}

/**
 * Cột `birthday`/`national_id_issued_date` là DATE nhưng server (Go `*time.Time`)
 * trả kèm giờ (`1964-05-11T00:00:00Z`), nên phải cắt về `YYYY-MM-DD` trước khi
 * format — không cắt thì màn hình in ra "08:00:00 11/5/1964", giờ đó là rác.
 * Để trống thì trả undefined cho ô hiện "—".
 */
function dateText(value?: string): string | undefined {
    return value ? formatVN(toDateInput(value)) : undefined
}
